import React, { useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Field, reduxForm } from "redux-form";
import { Button, Form, FormGroup, Alert, Label, Col, Row } from "reactstrap";
import {
  InputText,
  Loading,
  Dropdown,
  Validations,
  Valert,
} from "../components";
import homeActions from "../actions/homeActions";
import { isLoggedIn, getAccessToken } from "../services/AuthenticationService";
import queryString from "../services/QueryStringService";
import CryptoJS from "crypto-js";
import moment from "moment";
import { USER_LANG, statusEnum } from "../actions/constant";
import i18n from "../i18n";
import i18next, { t } from "i18next";
import "./styles/authModern.css";
import config from "../config";

const REDIRECT_URL_KEY = "redirectUrl";
const languageOptions = [
  { value: "en-US", label: "English", id: "1200000000001000022" },
  {
    value: "zh-CN",
    label: "Chinese (Simplified)",
    id: "1200000000001000015",
  },
  {
    value: "zh-TW",
    label: "Chinese (Traditional)",
    id: "1200000000001000016",
  },
];

const LANG_MAP = {
  '1200000000001000022': 'en-US',
  '1200000000001000015': 'zh-CN',
  '1200000000001000016': 'zh-TW',
};
const mapLangIdToI18n = (id) => LANG_MAP[id] || null;

let LoginForm = (props) => {
  const { t, handleLanguageChange, handleSubmit, showPassword, showPass } =
    props;

  return (
    <Form onSubmit={handleSubmit}>
      <Row style={{ marginLeft: "22.8rem", marginBottom: "8px" }}>
        <Col>
          <Field
            name={""}
            component={Dropdown}
            placeholder={t("profile.LANGUAGE")}
            options={languageOptions}
            onChange={handleLanguageChange}
          />
        </Col>
      </Row>
      <FormGroup>
        <Field
          component={InputText}
          type="text"
          name="email"
          className="email"
          placeholder={t("general.EMAIL_OR_PHONE")}
          validate={[Validations.required, Validations.email_phone]}
        />
      </FormGroup>
      <FormGroup>
        <Field
          component={InputText}
          type={showPass ? "text" : "password"}
          name="password"
          placeholder={t("general.PASSWORD")}
          validate={[Validations.required]}
          autoComplete="off"
          style={{ paddingRight: 36 }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div></div>
          <div
            style={{
              marginTop: "-25px",
              display: "flex",
              cursor: "pointer",
              marginRight: "10px",
              width: "20px",
              height: "25px",
            }}
            onClick={showPassword}
          >
            <i
              className={showPass ? "far fa-eye" : "far fa-eye-slash"}
              style={{ color: "#077bff" }}
            />
          </div>
        </div>
      </FormGroup>
      <FormGroup>
        <Label check>
          <Field component="input" type="checkbox" name="isRememberMe" />{" "}
          {t("home.KEEP_LOGIN")}
        </Label>
      </FormGroup>
      <FormGroup>
        <Button className="button-submit" color="primary">
          {t("home.LOGIN")}
        </Button>
      </FormGroup>
      <p>
        {t("home.NOT_A_MEMBER")} <a href="/signup">{t("home.SIGN_UP_NOW")}</a>
      </p>
      <p>
        <a href="/forgotpassword">{t("home.FORGOT_PASSWORD")}</a>
      </p>
    </Form>
  );
};

LoginForm = reduxForm({ form: "formLogin", touchOnBlur: false })(LoginForm);

const HomePage = (props) => {
  // const { t } = useTranslation();

  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [selectedLang, setSelectedLang] = useState("");
  const [pageActive, setPageActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageActiveColor, setPageActiveColor] = useState("");
  const [pageActiveMessage, setPageActiveMessage] = useState("");

  useEffect(() => {
    const values = queryString.parse(props.location.search);
    const langId = values.lang || getParameterByName('lang'); // support both helpers
    const langCode = mapLangIdToI18n(langId);

    // 1) Set language ASAP so the page (and Login later) loads in the right locale
    if (langCode) {
      i18n.changeLanguage(langCode);
      try {
        // i18next default storage key so other tabs pick it up
        localStorage.setItem('i18nextLng', langCode);
      } catch (e) {}
    }

    const init = async () => {
      const userSymptoms = JSON.parse(localStorage.getItem("user"));
      let startdate = moment().startOf("day").unix() * 1000;
      const _encKey = config.encryptionKey;

      if (isLoggedIn() && typeof values.redirectUrl === "undefined") {
        setLoading(true);
        await props.checkForData(startdate, userSymptoms._id);
        setLoading(false);
        const todaysData = JSON.parse(localStorage.getItem("todaysData"));
        handleTodaysData(todaysData);
      } else if (
        isLoggedIn() &&
        typeof values.redirectUrl === "undefined" &&
        userSymptoms.usersymptoms.length === 0
      ) {
        props.history.push("/symptom");
      } else {
        handleRedirect(values.redirectUrl, _encKey);
      }
      props.setDefaultState();
      localStorage.removeItem("todaysData");
    };

    init();

    // ---- Account Activation Path ----
    const code       = values.code       || getParameterByName("code");
    const subscriber = values.subscriber || getParameterByName("subscriber");

    if (code && subscriber) {
      props.activeUser({ code, subscriber, lang: langId }, (data) => {
        setPageActive(data.status);
        setPageActiveColor(data.status ? "success" : "danger");
        setPageActiveMessage(data.data.message.toUpperCase());

        if (data.status) {
          // Open Login in a new tab with language preserved
          const loginUrl = new URL("/login", window.location.origin);
          if (langId)   loginUrl.searchParams.set("lang", langId);       // backend’s ID
          if (langCode) loginUrl.searchParams.set("lng",  langCode);   // i18next query detector, if enabled

          // Also safe because we already set localStorage('i18nextLng')
          window.open(loginUrl.toString(), "_blank", "noopener,noreferrer");
        }
      });
    }

  }, []);

  useEffect(() => {
    if (props.status === statusEnum.SUCCESS) {
      handleSuccessRedirect();
    }
  }, [props.status]);

  const handleTodaysData = (todaysData) => {
    if (todaysData) {
      if (todaysData.symptoms && todaysData.symptoms.length > 0) {
        navigateToPage(todaysData, "/symptom", "/dailyinputsummary");
      } else if (todaysData.symptoms.length === 0) {
        navigateToPage(todaysData, "/symptom", "/dailyinputsummary");
      }
    }
  };

  const navigateToPage = (todaysData, symptomPage, summaryPage) => {
    if (
      todaysData.food_ingredients.length === 0 &&
      todaysData.activities.length === 0 &&
      todaysData.drugs.length === 0 &&
      todaysData.locations.length === 0 &&
      todaysData.pcp_ingredients.length === 0
    ) {
      window.location.href = symptomPage;
    } else {
      window.location.href = summaryPage;
    }
  };

  const handleRedirect = (redirectUrl, encKey) => {
    if (typeof redirectUrl !== "undefined") {
      const _strToDec = decodeURIComponent(redirectUrl);
      const _bytes = CryptoJS.AES.decrypt(_strToDec, encKey);
      localStorage.setItem(
        REDIRECT_URL_KEY,
        _bytes.toString(CryptoJS.enc.Utf8)
      );
    }
  };

  const handleSuccessRedirect = async () => {
    const _redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);
    setLoading(true);
    const userSymptoms = JSON.parse(localStorage.getItem("user"));
    if (_redirectUrl) {
      const _encStr = CryptoJS.AES.encrypt(
        getAccessToken(),
        config.encryptionKey
      );
      window.location.href = `${_redirectUrl}&${
        config.serverUserLoginPayload3Name
      }=${encodeURIComponent(_encStr)}`;
    } else if (userSymptoms.usersymptoms.length > 0) {
      let startdate = moment().startOf("day").unix() * 1000;
      await props.checkForData(startdate, userSymptoms._id);
      setLoading(false);
      const todaysData = JSON.parse(localStorage.getItem("todaysData"));
      handleTodaysData(todaysData);
    } else {
      window.location.href = "/symptom";
    }
  };

  const getParameterByName = (name, url = window.location.href) => {
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    return results[2] ? decodeURIComponent(results[2].replace(/\+/g, " ")) : "";
  };

  const handleSubmit = async (values) => {
    let lang_id;
    USER_LANG.forEach((item) => {
      if (item.code === i18n.language) {
        localStorage.setItem("ChangeLang", item.value);
        lang_id = item.value;
      }
    });
    await localStorage.setItem("i18nextLng", i18n.language);
    props.loginSubmit(values, lang_id);
  };

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    localStorage.setItem("i18nextLng", selectedLanguage);
    sessionStorage.setItem("i18nextLng", selectedLanguage);
    window.location.reload();
  };

  const showPassword = () => setShowLoginPassword(!showLoginPassword);

  return (
    <div className="vPage flex auth-modern">
      <div className="page-wrapper">
        <Valert
          display={props.invalid}
          title={
            props.isReachAttempt
              ? t("home.REACH_ATTEMPT_TITLE")
              : t("home.INVALID_ERROR_TITLE")
          }
          question={
            props.isReachAttempt
              ? t("home.REACH_ATTEMPT_TEXT")
              : t("home.INVALID_ERROR_TEXT")
          }
          positiveText={t("general.OK_PROMPT_TEXT")}
          readOnly={true}
        />
        <Loading isLoading={props.isLoading || loading} />
        <div className="justify-content-center">
          <div className="login-box">
            {props.status !== statusEnum.ERROR && pageActive && (
              <Alert color={pageActiveColor}>
                {t(`home.${pageActiveMessage}`)}
              </Alert>
            )}
            <LoginForm
              showPassword={showPassword}
              showPass={showLoginPassword}
              onSubmit={handleSubmit}
              t={t}
              handleLanguageChange={handleLanguageChange}
              selectedLang={selectedLang}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({ ...state.home });

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      loginSubmit: homeActions.loginSubmit,
      checkForData: homeActions.checkForData,
      setDefaultState: homeActions.setDefaultState,
      activeUser: homeActions.activeUser,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(HomePage);



// WEBPACK FOOTER //
// ./src/pages/HomePage.js